import { useState, useEffect, useRef, useCallback } from 'react';

interface SlideItem {
  type: 'image' | 'video';
  src: string;
  alt?: string;
}

const slides: SlideItem[] = [
  {
    type: 'video',
    src: 'https://storage.readdy-site.link/project_files/9e4a4ca5-4366-4703-9301-bafa1f14e1f9/4d59be5a-7c91-4da8-9029-12aa5f40af83_81f90afc6375d3b8db067c299a8f6058.mp4',
    alt: '华东交通大学科技光线',
  },
];

const AUTO_INTERVAL = 5000;

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const goTo = useCallback((idx: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(idx);
    window.setTimeout(() => setIsTransitioning(false), 700);
  }, [isTransitioning]);

  const next = useCallback(() => {
    const nextIdx = (current + 1) % slides.length;
    goTo(nextIdx);
  }, [current, goTo]);

  useEffect(() => {
    timerRef.current = window.setTimeout(next, AUTO_INTERVAL);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [next]);

  // Handle video play/pause based on current slide
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (slides[current].type === 'video') {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [current]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (bgRef.current) {
        bgRef.current.style.transform = `translateY(${scrollY * 0.3}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative h-[420px] md:h-[520px] overflow-hidden">
      {/* ━━ Background Slides (Carousel with Focus Effect) ━━ */}
      <div
        ref={bgRef}
        className="absolute inset-0 scale-110"
        style={{ willChange: 'transform' }}
      >
        {slides.map((slide, index) => {
          const isActive = index === current;
          return (
            <div
              key={index}
              className="absolute inset-0 transition-opacity duration-700 ease-in-out"
              style={{
                opacity: isActive ? 1 : 0,
                animation: isActive
                  ? 'focus-settle 0.7s ease-out forwards, focus-breathe 6s ease-in-out 0.7s infinite'
                  : 'none',
                willChange: 'transform, opacity, filter',
              }}
            >
              {slide.type === 'video' ? (
                <video
                  ref={videoRef}
                  src={slide.src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  className="w-full h-full object-cover object-center"
                  aria-label={slide.alt}
                />
              ) : (
                <img
                  src={slide.src}
                  alt={slide.alt || ''}
                  className="w-full h-full object-cover object-center"
                />
              )}
            </div>
          );
        })}
      </div>

      {/* ━━ Sunlight effect from top-left ━━ */}
      <div
        className="absolute inset-0 pointer-events-none z-[5]"
        style={{
          background: 'radial-gradient(ellipse at 8% 12%, rgba(255,235,180,0.30) 0%, rgba(255,215,130,0.12) 30%, rgba(255,195,100,0.04) 55%, transparent 72%)',
        }}
      />

      {/* ━━ Dark vignette for text contrast ━━ */}
      <div
        className="absolute inset-0 z-[6] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.15) 65%, rgba(0,0,0,0.45) 100%)',
        }}
      />

      {/* ━━ Title Overlay ━━ */}
      <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
        <div className="w-full max-w-[1800px] mx-auto px-4 md:px-8">
          <div className="text-center">
            {/* ── 成果名称 ── */}
            <div className="mb-4 md:mb-6">
              <h1
                className="text-[38px] md:text-[54px] lg:text-[66px] font-heading font-black text-[#C90000] leading-[1.2] tracking-[0.02em] max-w-[1800px] mx-auto"
              >
                学科赋能、需求牵引、交叉融合：<br />轨道交通特色土建类一流专业建设探索与实践
              </h1>
            </div>

            {/* ── Divider ── */}
            <div className="flex items-center justify-center gap-4 mb-4 md:mb-5">
              <span className="block h-px w-14 md:w-28 bg-white/50" />
              <span className="w-3 h-3 rounded-full bg-accent-500" style={{ boxShadow: '0 0 16px rgba(255,200,100,0.6), 0 0 32px rgba(255,200,100,0.3)' }} />
              <span className="block h-px w-14 md:w-28 bg-white/50" />
            </div>

            {/* ── 完成人 ── */}
            <div
              className="text-left max-w-[840px] mx-auto"
            >
              <p
                className="text-[19px] md:text-[20px] text-[#C90000] leading-[2.0] tracking-[0.06em] font-semibold"
              >
                完成人：徐长节，庄海洋，麻宏强，许弘毅，许紫刚，张鹏飞，姜&nbsp;&nbsp; 楠，张凯迪，
              </p>
              <p
                className="text-[19px] md:text-[20px] text-[#C90000] leading-[2.0] tracking-[0.06em] font-semibold"
                style={{ paddingLeft: '4.3em' }}
              >
                王永祥，冯玉林，胡锋平，吴必涛，石钰锋，张&nbsp;&nbsp; 豫，胡伟成
              </p>
            </div>

            {/* ── 完成单位 ── */}
            <p
              className="mt-3 md:mt-5 text-[18px] md:text-[20px] text-[#C90000] tracking-[0.1em] font-bold"
            >
              完成单位：华东交通大学
            </p>
          </div>
        </div>
      </div>

      {/* ━━ Slide Indicators ━━ */}
      <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            aria-label={`切换到第 ${index + 1} 张`}
            className="relative"
          >
            <span
              className={`block h-[6px] rounded-full transition-all duration-500 ${
                index === current
                  ? 'w-8 bg-white/25'
                  : 'w-2 bg-white/40 hover:bg-white/60'
              }`}
            />
            {index === current && (
              <span className="absolute top-0 left-0 h-[6px] rounded-full bg-white overflow-hidden" style={{ width: '32px' }}>
                <span
                  className="block h-full w-0 bg-white"
                  style={{
                    animation: 'carousel-progress 5s linear forwards',
                  }}
                />
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ━━ Bottom Fade to next section ━━ */}
      <div
        className="absolute bottom-0 left-0 right-0 pointer-events-none z-10"
        style={{
          height: '100px',
          background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.97))',
        }}
      />
    </section>
  );
}